import { Component, inject, Input, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { SidebarService } from '@doc/api/server-bo';

@Component({
  selector: 'doc-common-title',
  templateUrl: './common-title.component.html',
  styleUrls: ['./common-title.component.scss'],
  standalone: false,
})
export class CommonTitleComponent implements OnInit {
  @Input() title: string = '';
  @Input() back: boolean = false;
  @Input() documentId: string = '';
  @Input() level1: string = '';
  @Input() level2: string = '';
  /** When false, breadcrumbs from the sidebar service are hidden. */
  @Input() showBreadcrumb: boolean = true;

  breadcrumbs: any[] = [];

  get hasTitle(): boolean {
    return !!(this.title && String(this.title).trim());
  }

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private sidebarService = inject(SidebarService);

  currentMode: any;
  lookupType: any;
  typeId: any;
  levelType: any;
  readonly breadcrumbLabelMaxLength = 30;

  ngOnInit(): void {
    this.sidebarService.breadcrumb$.subscribe(items => {
      this.breadcrumbs = Array.isArray(items) ? items : [];
    });

    this.route.queryParams.subscribe(params => {
      this.currentMode = params['doc'];
      this.lookupType = params['lookupType'];
      this.typeId = params['type'];
    });
  }

  isValidLink(link: any): boolean {
    return typeof link === 'string' && link.trim().length > 0;
  }

  canNavigateBreadcrumb(item: any, isLast: boolean): boolean {
    if (!this.isValidLink(item?.link)) {
      return false;
    }

    const hasQueryParams = !!item?.queryParams && Object.keys(item.queryParams).length > 0;
    return !isLast || hasQueryParams;
  }

  getBreadcrumbIcon(item: any): string {
    return item.icon === 'home'
      ? 'custom:home'
      : item.icon === 'file' ? '' : 'custom:folder-breadcrumb';
  }

  formatBreadcrumbLabel(label: unknown, maxLength: number = this.breadcrumbLabelMaxLength): string {
    const safeLabel = typeof label === 'string' ? label : String(label ?? '');
    return safeLabel.length > maxLength
      ? `${safeLabel.slice(0, maxLength)}...`
      : safeLabel;
  }

  getFullBreadcrumbLabel(label: unknown): string {
    return typeof label === 'string' ? label : String(label ?? '');
  }

  redirectForm() {
    if (this.currentMode === 'edit') {
      const lastRoute = localStorage.getItem('lastRoute');
      const lastQueryRaw = localStorage.getItem('lastQuery');
      const lastQuery = lastQueryRaw ? JSON.parse(lastQueryRaw) : null;
      if (lastRoute) {
        if (lastQuery && Object.keys(lastQuery).length > 0) {
          this.router.navigate([lastRoute], {
            queryParams: lastQuery,
          });
        } else {
          this.router.navigate([lastRoute]);
        }
      } else {
        this.router.navigate(
          ['/main/document/document-details/', this.documentId],
          { relativeTo: this.route }
        );
      }
    } else if (this.currentMode === 'update') {
      this.router.navigate([
        `/main/document/document-details/${this.documentId}`]);
    } else if (this.currentMode === undefined) {
      if (this.router.url.includes('/main/document/processed-document/')) {
        this.router.navigate(['/main/document/processed-documents']);
      } else if (this.router.url.includes('/level')) {
        this.levelType = this.route.snapshot.paramMap.get('level') || '';
        this.router.navigate([`/main/hierarchy/level/${this.levelType}`]);
      } else if (
        this.router.url.includes('/add-lookup') ||
        this.router.url.includes('/edit-lookup')
      ) {
        this.router.navigate([
          `/main/admin-config/${this.lookupType}/${this.typeId}`]);
      } else if (
        this.router.url.includes('/edit/') ||
        this.router.url.includes('/view/')
      ) {
        const relativePath = '../../';
        this.router.navigate([relativePath], { relativeTo: this.route });
      } else if (this.router.url.includes('/translation-template')) {
        this.router.navigate(['/main/admin-config/translation-template-list']);
      } else {
        const relativePath = '../';
        this.router.navigate([relativePath], { relativeTo: this.route });
      }
    } else {
      const relativePath = '../';
      this.router.navigate([relativePath], { relativeTo: this.route });
    }
  }
}
